import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react';
import {getTimeDuration} from '../../../utils/PlayoffUtils';
import {Court, League} from '../../../types';

interface ScheduleGameModalProps {
  isOpen: boolean;
  onClose: () => void;
  leagues?: League[];
  courts?: Court[];
  selectedLeague?: number;
  onLeagueChange: (leagueId: number) => void;
  selectedCourt?: number;
  onCourtChange: (courtId: number) => void;
  startTime: string;
  onStartTimeChange: (time: string) => void;
  endTime: string;
  onEndTimeChange: (time: string) => void;
  timeError: string;
  isLoading: boolean;
  onSubmit: () => void;
}

export const ScheduleGameModal: React.FC<ScheduleGameModalProps> = ({
                                                                      isOpen,
                                                                      onClose,
                                                                      leagues,
                                                                      courts,
                                                                      selectedLeague,
                                                                      onLeagueChange,
                                                                      selectedCourt,
                                                                      onCourtChange,
                                                                      startTime,
                                                                      onStartTimeChange,
                                                                      endTime,
                                                                      onEndTimeChange,
                                                                      timeError,
                                                                      isLoading,
                                                                      onSubmit,
                                                                    }) => {
  const duration = getTimeDuration(startTime, endTime);
  const isFormValid = selectedLeague && selectedCourt && startTime && endTime && !timeError;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay/>
      <ModalContent>
        <ModalHeader>Schedule Playoff Game</ModalHeader>
        <ModalCloseButton/>
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>League</FormLabel>
              <Select
                placeholder="Select league"
                value={selectedLeague}
                onChange={(e) => onLeagueChange(Number(e.target.value))}
              >
                {leagues?.map((league) => (
                  <option key={league.id} value={league.id}>
                    {league.name}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Court</FormLabel>
              <Select
                placeholder="Select court"
                value={selectedCourt}
                onChange={(e) => onCourtChange(Number(e.target.value))}
              >
                {courts?.map((court) => (
                  <option key={court.id} value={court.id}>
                    {court.name}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl isRequired isInvalid={!!timeError}>
              <FormLabel>Start Time</FormLabel>
              <Input
                type="datetime-local"
                value={startTime}
                onChange={(e) => onStartTimeChange(e.target.value)}
              />
            </FormControl>

            <FormControl isRequired isInvalid={!!timeError}>
              <FormLabel>End Time</FormLabel>
              <Input
                type="datetime-local"
                value={endTime}
                onChange={(e) => onEndTimeChange(e.target.value)}
              />
              {timeError && <FormErrorMessage>{timeError}</FormErrorMessage>}
              {!timeError && startTime && endTime && (
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Duration: {duration}
                </Text>
              )}
            </FormControl>

            <Box
              w="100%"
              p={3}
              bg="blue.50"
              borderRadius="md"
              borderLeft="4px solid"
              borderColor="blue.400"
            >
              <Text fontSize="sm" color="blue.800">
                <strong>Note:</strong> The system will automatically assign this time slot to
                the next available unscheduled playoff game in the selected league.
              </Text>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={onSubmit}
            isLoading={isLoading}
            loadingText="Scheduling..."
            isDisabled={!isFormValid}
          >
            Enqueue Game
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
